import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CircularProgress,
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  Stack,
  Box,
  useTheme,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material"; // Import Clear icon
import AddIcon from "@mui/icons-material/Add";
import { ZoomIn as ViewIcon, Edit as EditIcon } from "@mui/icons-material";

import { readAsyncStorageValues } from "features/service/service.slice";
import { getAllVendors, getAllSupplierVendor } from "features/appUsers/appUsers.slice";
import "../../App.css";
import "../../stylesheet/charts.css";
import { useNavigate } from "react-router-dom";
import { Links } from "utils/pageLinkNames";
import Header from "component/Header";
import DataGridTable from "component/DataGridTable";
import SendIcon from "@mui/icons-material/Send";
import TableSkeleton from "component/Loaders/TableSkeleton";

const Vendors = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const { allVendorsList, allSupplierVendorsList } = useSelector((state) => state.appUsers);
  const { mode } = useSelector((state) => state.globalState);

  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    dispatch(getAllVendors());
    dispatch(getAllSupplierVendor());
  }, [dispatch]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleFilterChange = (event) => {
    setActiveFilter(event.target.value);
  };

  const handleClearFilter = () => {
    setActiveFilter("");
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const gridVendorAction = (data) => {
    return (
      <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.EDIT_VENDOR}/` + data.id)}>
        <Tooltip title="View">
          <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
        </Tooltip>
      </IconButton>
    );
  };

  const vendorGrid = [
    {
      field: "customerName",
      headerName: "Name",
      minWidth: 50,
      flex: 0.2,
    },
    {
      field: "customerType",
      headerName: "Type",
      minWidth: 50,
      flex: 0.2,
    },
    {
      field: "emailAddress",
      headerName: "Email",
      minWidth: 30,
      flex: 0.2,
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 50,
      flex: 0.2,
    },
    {
      field: "active",
      headerName: "Active",
      minWidth: 30,
      flex: 0.1,
      valueGetter: (params) => (params.value ? "Yes" : "No"),
    },
    {
      headerName: "",
      minWidth: 30,
      flex: 0.2,
      renderCell: gridVendorAction,
      sortable: false,
      filterable: false,
    },
  ];

  const filteredData = (activeTab === 0 ? allVendorsList.data : allSupplierVendorsList.data) || [];
  const filteredAndSearchedData = filteredData
    .filter((item) => {
      if (activeFilter === "") return true;
      return item.active === (activeFilter === "Yes");
    })
    .filter((item) => Object.values(item).join(" ").toLowerCase().includes(searchValue.toLowerCase()));

  return (
    <div>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header titles={["Vendors/Suppliers"]} />
      </Stack>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} gap={1} alignItems={"center"} my={2}>
          <Box>
            <Button
              startIcon={<AddIcon />}
              sx={{ height: "self" }}
              autoCapitalize="none"
              variant="contained"
              onClick={() => navigate(`/${Links.ADD_VENDOR}/`)}
            >
              Add
            </Button>
          </Box>

          <OutlinedInput
            id="outlined-adornment-password"
            value={searchValue}
            onChange={(event) => handleSearchChange(event)}
            placeholder="Search..."
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label="toggle password visibility" edge="end">
                  <Search />
                </IconButton>
              </InputAdornment>
            }
            sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
            <InputLabel
              sx={{
                backgroundColor: mode === "dark" ? "none" : "#fff",
                paddingLeft: "7px",
                paddingRight: "7px",
                "&.Mui-focused": {
                  color: mode === "dark" ? "#fff" : "#008080",
                },
              }}
            >
              Active
            </InputLabel>
            <Select
              value={activeFilter}
              onChange={handleFilterChange}
              endAdornment={
                <InputAdornment position="center">
                  {activeFilter && (
                    <IconButton onClick={handleClearFilter} size="small" sx={{ p: 1.3 }}>
                      <Clear />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="Vendor and Supplier Tabs" centered>
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                flex: 2,
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label="Vendors"
            />
            <Tab
              sx={{
                fontWeight: "bold",
                marginRight: "20px",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                flex: 2,
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label="Suppliers"
            />
          </Tabs>
        </Box>
        {allVendorsList.isLoading || allSupplierVendorsList.isLoading ? (
          <TableSkeleton rows={8} columns={vendorGrid.length} />
        ) : (
          <DataGridTable rowId={"id"} data={filteredAndSearchedData} columnAttributes={vendorGrid} />
        )}
      </Box>
    </div>
  );
};

export default Vendors;
